import React, { useState, useCallback, useMemo } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Collapse, IconButton, Box, Typography, Card, CardContent,
  Checkbox, CircularProgress, Grid, Avatar, Button, TextField
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import LocationCityRoundedIcon from '@mui/icons-material/LocationCityRounded';
import CancelIcon from '@mui/icons-material/Cancel';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import theme from '../../../material-ui/theme';

import { useFetch } from '../../../hooks/useFetch';

import {
  panelsActiveItem,
  panelsRemoveActiveItemById
} from '../../../reducers/panels/panelsReducer';
import { startContractsActive } from '../../../reducers/contracts/contractsActions';
import { LoadFromToTimePickers } from '../../common/LoadFromToTimePickers/LoadFromToTimePickers';

export const DeviceTableNoMeasures = () => {
  const contracts = useSelector(state => state.contracts.contracts);
  const panels = useSelector(state => state.panels.panels);

  const [state, setState] = useState({ from: null, to: null, dateFormat: 'YYYY-MM-DD' });
  const [searchQuery, setSearchQuery] = useState('');

  const handleState = useCallback(
    (value) => {
      setState(value);
    },
    [setState]
  );

  const { from, to } = state;

  const url =
    from && to
      ? `contracts/unsentalarms?from=${from}&to=${to}`
      : null;

  const { data, loading } = useFetch(url);


  const unsentData = Array.isArray(data) ? data : [];

  const groupedData = useMemo(() => {
    if (!contracts || !unsentData) return [];
    return contracts
      .filter(c => c.name.toLowerCase().includes(searchQuery.toLowerCase()))
      .map(contract => {
        const related = unsentData.filter(item => item.contract_id === contract.id);
        const uniquePanels = [...new Set(related.map(r => r.panel_id))];

        return {
          contractId: contract.id,
          name: contract.name,
          totalDevices: uniquePanels.length,
          rows: related,
        };
      });
  }, [contracts, unsentData, searchQuery]);

  if (!contracts || !panels) return <div>Cargando datos...</div>;


  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={6}>
          <TextField
            label="Buscar contrato"
            variant="outlined"
            fullWidth
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Box sx={{ float: 'right' }}>
            <LoadFromToTimePickers handleState={handleState} defaultRange="Last month" />
          </Box>
        </Grid>
      </Grid>

      {!from || !to ? (
        <Typography sx={{ mt: 3 }} variant="body2">
          Selecciona un rango de fechas para mostrar dispositivos sin medidas.
        </Typography>
      ) : loading ? (
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ mt: 3 }}>
          <Table aria-label="collapsible table">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell rowSpan={2} style={{ textAlign: 'left', fontWeight: 'bold', fontSize: '1.05rem', borderRight: '1px solid black' }}>
                  <LocationCityRoundedIcon style={{ fontSize: '1.05rem', color: 'blue', marginRight: '0.5rem' }} />
                  Contrato
                </TableCell>
                <TableCell rowSpan={2} style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '1.05rem' }}>
                  <CancelIcon style={{ fontSize: '1.05rem', color: 'red', marginRight: '0.5rem' }} />
                  Dispositivos sin medida
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groupedData.map((row) => (
                <CollapsibleRow key={row.contractId} row={row} panels={panels} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

const CollapsibleRow = ({ row, panels }) => {
  const [open, setOpen] = useState(false);
  const [selectedPanelId, setSelectedPanelId] = useState(null);
  const [sortOrder, setSortOrder] = useState('desc');

  const dispatch = useDispatch();
  const activePanels = useSelector(state => state.panels.active);
  const activeContract = useSelector(state => state.contracts.activeContract);

  const isRowActive = row.contractId === activeContract?.id;

  const handleToggleContract = () => {
    setOpen(prev => !prev);
  };

  const handleActivateContract = (e) => {
    e.stopPropagation();
    if (!isRowActive) {
      dispatch(startContractsActive(row.contractId));
    }
  };

  const handleTogglePanelCard = (panelId) => {
    setSelectedPanelId(prev => (prev === panelId ? null : panelId));
  };

  const handleSelect = (panelId, isChecked) => {
    if (!isRowActive) return;
    if (isChecked) {
      dispatch(panelsActiveItem({ id: panelId }));
    } else {
      dispatch(panelsRemoveActiveItemById(panelId));
    }
  };

  const handleSortClick = () => {
    setSortOrder(prev => (prev === 'desc' ? 'asc' : 'desc'));
  };

  // Obtener paneles únicos con número de alarmas
  const sortedPanelIds = [...new Set(row.rows.map(r => r.panel_id))].sort((a, b) => {
    const countA = row.rows.filter(r => r.panel_id === a).length;
    const countB = row.rows.filter(r => r.panel_id === b).length;
    return sortOrder === 'desc' ? countB - countA : countA - countB;
  });

  return (
    <>
      <TableRow
        onClick={handleToggleContract}
        sx={{
          cursor: 'pointer',
          backgroundColor: isRowActive ? '#f0f0f0' : 'inherit'
        }}
      >
        <TableCell>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleToggleContract();
            }}
          >
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Button
            onClick={handleActivateContract}
            variant="outlined"
            disabled={isRowActive}
            style={{
              fontWeight: 'bold',
              color: isRowActive ? 'white' : 'black',
              backgroundColor: isRowActive ? '#f39c12' : 'transparent',
              borderColor: isRowActive ? '#f39c12' : 'rgba(0, 0, 0, 0.23)',
              textTransform: 'none'
            }}
          >
            {row.name}
          </Button>
        </TableCell>
        <TableCell align="center">{row.totalDevices}</TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={6} sx={{ paddingBottom: 0, paddingTop: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Typography variant="h6" gutterBottom>
                Dispositivos con ausencia de medidas cuarto horarias
              </Typography>

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Seleccionar</TableCell>
                    <TableCell>Nombre del dispositivo</TableCell>
                    <TableCell
                      align="center"
                      onClick={handleSortClick}
                      sx={{
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        color: '#1976d2',
                        userSelect: 'none',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      Nº de días con huecos de comunicación en las fechas seleccionadas
                      {sortOrder === 'desc' ? <ArrowDropDownIcon /> : <ArrowDropUpIcon />}
                    </TableCell>


                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedPanelIds.map(panelId => {
                    const device = panels.find(p => p.id === panelId);
                    const deviceAlarms = row.rows.filter(r => r.panel_id === panelId);
                    const isSelected = activePanels.some(p => p.id === panelId);

                    return (
                      <React.Fragment key={panelId}>
                        <TableRow
                          onClick={() => handleTogglePanelCard(panelId)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell>
                            <Checkbox
                              checked={isSelected}
                              disabled={!isRowActive}
                              onChange={(e) => handleSelect(panelId, e.target.checked)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Avatar
                                src={theme.devicesIcons.panel.avatar}
                                alt="Panel Icon"
                                style={{ width: '24px', height: '24px', marginRight: '0.5rem' }}
                              />
                              {device ? device.name : `CM con ID: ${panelId}`}
                            </Box>
                          </TableCell>
                          <TableCell align="center">{deviceAlarms.length}</TableCell>
                        </TableRow>

                        {selectedPanelId === panelId && (
                          <TableRow>
                            <TableCell colSpan={6}>
                              <Card variant="outlined" sx={{ my: 1 }}>
                                <CardContent>
                                  <Typography variant="subtitle1" gutterBottom>
                                    Alarmas del dispositivo
                                  </Typography>
                                  <ul style={{ paddingLeft: '1.2rem', margin: 0 }}>
                                    {[...deviceAlarms]
                                      .sort((a, b) => new Date(a.date) - new Date(b.date))
                                      .map(alarm => (
                                        <li key={alarm.id}>{alarm.name}</li>
                                      ))}
                                  </ul>
                                </CardContent>
                              </Card>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

